import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { useSession } from "./lib/auth-client";
import { trpc, createTRPCClient } from "./lib/trpc";
import { router, type RouterContext } from "./router";
import { useUserStore } from "./store/user-store";
import { initObservability } from "./lib/observability";
import "./lib/i18n";
import "./index.css";

const queryClient = new QueryClient();
const trpcClient = createTRPCClient();

function InnerApp() {
  const { data: session, isPending: authPending } = useSession();
  const { data: me, isPending: mePending } = trpc.me.get.useQuery(undefined, {
    enabled: !!session?.user,
  });

  const isPending = authPending || (!!session?.user && mePending);

  // Re-evaluate route guards whenever the auth/me state changes so routes
  // pick up transitions like "just activated" or "just onboarded".
  const orgId = me?.orgId ?? null;
  const orgName = me?.orgName ?? null;
  const onboarded = me?.onboarded ?? false;
  const isManager = me?.isManager ?? false;
  const isSuperadmin = me?.isSuperadmin ?? false;
  const setOrgName = useUserStore((s) => s.setOrgName);
  const setIsManager = useUserStore((s) => s.setIsManager);
  const setIsSuperadmin = useUserStore((s) => s.setIsSuperadmin);
  useEffect(() => {
    setOrgName(orgName);
    setIsManager(isManager);
    setIsSuperadmin(isSuperadmin);
  }, [orgName, isManager, isSuperadmin, setOrgName, setIsManager, setIsSuperadmin]);

  const prevKey = useRef<string | null>(null);
  useEffect(() => {
    const key = `${isPending}|${session?.user?.id ?? ""}|${orgId ?? ""}|${onboarded}|${isManager}|${isSuperadmin}`;
    if (prevKey.current !== key) {
      prevKey.current = key;
      router.invalidate();
    }
  }, [isPending, session?.user?.id, orgId, onboarded, isManager, isSuperadmin]);

  return (
    <RouterProvider
      router={router}
      context={{
        auth: session?.user
          ? {
              user: session.user as RouterContext["auth"] extends { user: infer U } ? U : never,
              session: session.session as RouterContext["auth"] extends { session: infer S } ? S : never,
              orgId,
              onboarded,
              isManager,
              isSuperadmin,
            }
          : null,
        isPending,
      }}
    />
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <InnerApp />
        <Toaster position="bottom-center" richColors closeButton />
      </QueryClientProvider>
    </trpc.Provider>
  </React.StrictMode>,
);

initObservability();
